import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@repo/ui/src/lib/utils';
import { useClaimBottomBar } from '../stores/bottomBarStore';

/**
 * The one bottom selection bar (01-shell §6): a summary on the left, actions
 * on the right, fixed to the viewport bottom above the phone tab bar.
 *
 * It owns `useClaimBottomBar` on purpose. On phones the tab bar renders only
 * when nobody claims the bottom edge, so a selection bar that forgot to claim
 * would sit ON TOP of the tab bar instead of replacing it. Centralising the
 * claim here means adopting the bar and claiming the edge are the same act —
 * consumers must NOT call `useClaimBottomBar` themselves (two claims on one id
 * is exactly the drift this prevents).
 *
 * The hook runs before the `active` early return, so a consumer that unmounts
 * this component when its selection empties still releases the edge via the
 * hook's cleanup.
 */
export function SelectionBar({
  id,
  ariaLabel,
  active,
  summary,
  actions,
  lowered = false,
  stretch = false,
  children,
}: {
  /** Bottom-edge claim key — unique per bar ('bulk-actions', 'move-mode'). */
  id: string;
  ariaLabel: string;
  /** Both the claim condition and the render gate. */
  active: boolean;
  /** Omit for a summary-less bar (D2 phone write footer, no left-hand text):
   *  the actions row then owns the whole row width — see `stretch`. */
  summary?: ReactNode;
  actions: ReactNode;
  /** Drop to z-40 while an overlay (a Sheet at z-50) is open, so the bar sits
   *  UNDER it instead of covering its header on phones. */
  lowered?: boolean;
  /** `true`: the actions row grows to fill the bar instead of hugging the
   *  right edge (`ml-auto`), so a `flex-1` action inside it can actually
   *  expand to fill the remaining width — D2's phone write footer needs a
   *  full-width primary button next to a fixed-width "Bỏ qua", which the
   *  default right-hugging row can't produce (a `flex-1` child has nothing
   *  to grow INTO when its own parent is itself shrink-wrapped to content).
   *  Default `false` keeps every other consumer's compact right-aligned
   *  action cluster pixel-identical. */
  stretch?: boolean;
  /** Rendered inside the bar, after the summary/actions row — e.g. a picker
   *  Sheet that must stay mounted with it. */
  children?: ReactNode;
}) {
  useClaimBottomBar(id, active);
  if (!active) return null;

  // Portalled to document.body: `fixed` is positioned against the nearest
  // ancestor with a transform/filter/animation (a new containing block), not
  // necessarily the viewport. WorkbenchPage's root carries
  // `motion-safe:animate-[fade-up…both]` — `both` keeps the animation's
  // transform applied at rest, not just mid-run — so a bar rendered in place
  // would be pinned to that row instead of the screen bottom. The portal
  // sidesteps the whole class of ancestor forever, not just this one page.
  return createPortal(
    <div
      role="region"
      aria-label={ariaLabel}
      className={cn(
        'fixed inset-x-0 bottom-0 border-t border-surface-200 bg-surface-0/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur',
        lowered ? 'z-40' : 'z-[60]',
      )}
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3">
        {summary && (
          <div className="flex min-w-0 flex-wrap items-center gap-3 text-sm font-medium text-surface-900">
            {summary}
          </div>
        )}
        <div className={cn('flex flex-wrap items-center gap-2', stretch ? 'flex-1' : 'ml-auto')}>{actions}</div>
      </div>
      {children}
    </div>,
    document.body,
  );
}
